import { ArrowLeft, Info, Heart } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useState, useEffect } from "react";
import welcomeBg from "../../assets/homebg.png";
import { get } from "@/services/axiosUitils";
import { CHART_AXIS_TICK_LG, CHART_FONT_SIZES } from "../../utils/chartConfig";

const HeartRateDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("weekly");

  // Get selected date from FitnessPage navigation
  const selectedDate = location.state?.selectedDate || null;

  // API data states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [weeklyData, setWeeklyData] = useState<any[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState({
    resting_heart_rate: 0,
    zones: [],
    days: 7,
  });
  const [dateRange, setDateRange] = useState({
    start_date: "",
    end_date: "",
  });

  // Fetch heart rate data from API with selected date
  const fetchHeartRateData = async (type: "weekly" | "monthly") => {
    try {
      setLoading(true);
      setError(null);

      // Build API URL with date parameter if available
      const dateParam = selectedDate ? `&date=${selectedDate}` : "";
      const apiUrl = `fitbit_scan/heart-rate-graph/?type=${type}${dateParam}`;

      const response = await get(apiUrl);

      if (response.success) {
        setSummaryData(response.summary);
        setDateRange({
          start_date: response.start_date,
          end_date: response.end_date,
        });

        if (type === "weekly") {
          // Transform weekly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            day: item.day,
            resting: item.resting || 0, // Keep zero values for proper chart display
          }));
          setWeeklyData(chartData);
        } else {
          // Transform monthly API data to chart format
          const chartData = response.graph.map((item: any) => ({
            week: item.week,
            avg: item.avg || 0,
          }));
          setMonthlyData(chartData);
        }
      }
    } catch (err: any) {
      setError("Failed to fetch heart rate data");
    } finally {
      setLoading(false);
    }
  };

  // Handle tab change and fetch data
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "weekly") {
      fetchHeartRateData("weekly");
    } else if (tab === "monthly") {
      fetchHeartRateData("monthly");
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchHeartRateData("weekly");
  }, []);

  // Format date range for display
  const formatDateRange = () => {
    if (!dateRange.start_date || !dateRange.end_date) {
      return "";
    }

    const startDate = new Date(dateRange.start_date).toLocaleDateString(
      "en-US",
      {
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    );
    const endDate = new Date(dateRange.end_date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return `${startDate} - ${endDate}`;
  };

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-16"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate("/glucera/fitness", {
                state: {
                  selectedDate: selectedDate,
                  selectedDateObj: location.state?.selectedDateObj,
                },
              })
            }
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="text-center">
            <h1 className="text-title3 font-bold text-foreground">Heart Rate</h1>
            {selectedDate && (
              <p className="text-caption1 text-muted-foreground">
                {activeTab === "weekly" ? "Week of" : "Month of"}{" "}
                {new Date(selectedDate + "T00:00:00").toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="w-10" />
        </div>

        {/* Current Heart Rate */}
        <Card className="rounded-2xl border border-border bg-gradient-to-br from-red-50 to-red-100 p-6 shadow-lg dark:from-red-950 dark:to-red-900">
          <div className="text-center">
            <div className="mb-3 inline-flex h-20 w-20 items-center justify-center rounded-full bg-red-500/20">
              <Heart className="h-10 w-10 animate-pulse text-red-500" />
            </div>
            <p className="mb-2 text-footnote text-muted-foreground">
              Resting Heart Rate
            </p>
            <div className="mb-1 text-5xl font-bold text-foreground">
              {loading ? "..." : summaryData.resting_heart_rate || 0}
            </div>
            <p className="text-footnote text-muted-foreground">BPM</p>
            {dateRange.start_date && (
              <p className="mt-2 text-caption1 text-muted-foreground">
                {formatDateRange()}
              </p>
            )}
          </div>
        </Card>

        {/* Heart Rate Zones */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <h3 className="mb-4 text-callout font-semibold text-foreground">
            Heart Rate Zones
          </h3>
          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
              </div>
            ) : (
              summaryData.zones?.map((zone: any, i: number) => {
                const colors = [
                  "bg-blue-500",
                  "bg-yellow-500",
                  "bg-orange-500",
                  "bg-red-500",
                ];
                return (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`h-3 w-3 rounded-full ${colors[i] || "bg-gray-500"}`}
                      />
                      <div>
                        <p className="text-callout font-medium text-foreground">
                          {zone.name}
                        </p>
                        <p className="text-caption1 text-muted-foreground">
                          {zone.min}-{zone.max} BPM
                        </p>
                      </div>
                    </div>
                    <span className="text-callout font-medium text-foreground">
                      {zone.time_formatted}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border border-border bg-card shadow-lg">
          <div className="p-4">
            <div className="inline-flex w-full max-w-sm rounded-xl bg-muted p-1">
              <button
                onClick={() => handleTabChange("weekly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-callout font-medium transition-all duration-200 ${
                  activeTab === "weekly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => handleTabChange("monthly")}
                className={`flex-1 rounded-lg px-4 py-2.5 text-callout font-medium transition-all duration-200 ${
                  activeTab === "monthly"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-background/50 hover:text-foreground"
                }`}
              >
                Monthly
              </button>
            </div>
          </div>

          <div className="px-4 pb-4 pt-0">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                  <p className="text-footnote text-muted-foreground">
                    Loading heart rate data...
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="mb-2 text-footnote text-red-600">{error}</p>
                  <button
                    onClick={() =>
                      fetchHeartRateData(activeTab as "weekly" | "monthly")
                    }
                    className="text-footnote text-red-500 hover:text-red-600"
                  >
                    Try again
                  </button>
                </div>
              </div>
            )}

            {!loading && !error && activeTab === "weekly" && (
              <div>
                <h3 className="mb-4 text-callout font-semibold text-foreground">
                  Weekly Heart Rate ({weeklyData.length} days)
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                {weeklyData.length === 0 ? (
                  <p className="mb-4 text-callout text-muted-foreground">
                    No heart rate data available for this period.
                  </p>
                ) : weeklyData.every(d => d.resting === 0) ? (
                  <p className="mb-4 text-callout text-orange-600">
                    ⚠️ All heart rate values are 0 - please check your device
                    connection.
                  </p>
                ) : null}
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <YAxis
                      stroke="#6b7280"
                      tick={CHART_AXIS_TICK_LG}
                      domain={[
                        0,
                        (dataMax: number) => Math.max(dataMax + 10, 100),
                      ]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "2px solid hsl(var(--border))",
                        borderRadius: "12px",
                        color: "hsl(var(--foreground))",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      formatter={(value: any) => [`${value} BPM`, "Resting HR"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="resting"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ fill: "#ef4444", r: 4 }}
                      name="Resting HR"
                      connectNulls
                      strokeDasharray={
                        weeklyData.every(d => d.resting === 0) ? "5 5" : "0"
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {!loading && !error && activeTab === "monthly" && (
              <div>
                <h3 className="mb-4 text-callout font-semibold text-foreground">
                  Monthly Average Heart Rate ({monthlyData.length} weeks)
                  {dateRange.start_date && (
                    <span className="ml-2 text-footnote font-normal text-muted-foreground">
                      ({formatDateRange()})
                    </span>
                  )}
                </h3>
                {monthlyData.length === 0 && (
                  <p className="mb-4 text-callout text-gray-500">
                    No heart rate data available for this period.
                  </p>
                )}
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" stroke="#6b7280" tick={CHART_AXIS_TICK_LG} />
                    <YAxis
                      stroke="#6b7280"
                      tick={CHART_AXIS_TICK_LG}
                      domain={[0, "dataMax + 10"]}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "2px solid hsl(var(--border))",
                        borderRadius: "12px",
                        color: "hsl(var(--foreground))",
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      formatter={(value: any) => [`${value} BPM`, "Average HR"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="avg"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={{ fill: "#ef4444", r: 4 }}
                      name="Average HR"
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Card>

        {/* Information Card */}
        <Card className="rounded-2xl border border-border bg-card p-5 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-950">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="mb-2 text-callout font-semibold text-foreground">
                Understanding Heart Rate
              </h3>
              <p className="text-callout leading-relaxed text-muted-foreground">
                Your heart rate is measured by optical sensors on your Fitbit
                that detect blood flow through your wrist. Resting heart rate
                (RHR) is measured when you're inactive and indicates overall
                cardiovascular fitness. A lower RHR typically indicates better
                fitness. Heart rate variability (HRV) measures the variation in
                time between heartbeats and is an indicator of stress and
                recovery.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HeartRateDetail;
